import { getCharacterPlatformApiUrlFromEnv } from "@/lib/platformConfig.js"

export const CHAT_APP_EMBED_READY_EVENT = "chat-app:embed-ready"
export const CHAT_APP_PLATFORM_AUTH_EVENT = "chat-app:platform-auth"
export const CHAT_APP_PLATFORM_AUTH_ACK_EVENT = "chat-app:platform-auth-ack"
export const CHAT_APP_PLATFORM_DISCONNECT_EVENT = "chat-app:platform-disconnect"
export const CHAT_APP_PLATFORM_MEDIA_STATE_EVENT = "chat-app:platform-media-state"
export const CHAT_APP_PLATFORM_CONNECTION_STATUS_EVENT =
  "chat-app:platform-connection-status"
export const CHAT_APP_PLATFORM_TASK_EVENT = "chat-app:platform-task-event"
export const CHAT_APP_WORLD_TASKS_CHANGED_EVENT = "chat-app:world-tasks-changed"
export const CHAT_APP_EMBED_TIMEOUT_MS = 10000
export const CHAT_APP_EMBED_URL_OVERRIDE_STORAGE_KEY =
  "ego-world.chatAppEmbedUrl"
export const CHAT_APP_EMBED_SESSION_OVERRIDE_STORAGE_KEY =
  "ego-world.chatAppEmbedSession"

export function emitChatAppEmbedTrace(scope, event, detail) {
  const payload = {
    atMs: typeof performance !== "undefined" ? Math.round(performance.now()) : null,
    detail: detail ?? undefined,
    event,
    scope,
  }

  try {
    console.info(`[LatencyTrace] ${JSON.stringify(payload)}`)
  } catch {}
}

export function namespacePersonalAgentId(rawId) {
  const trimmed = typeof rawId === "string" ? rawId.trim() : ""
  if (!trimmed) return null
  if (trimmed.startsWith("personal:") || trimmed.startsWith("default:")) {
    return trimmed
  }
  return `personal:${trimmed}`
}

export function buildChatAppEmbedUrl(baseUrl) {
  const normalized = new URL(baseUrl)
  if (
    normalized.pathname.endsWith(".html") ||
    normalized.pathname.endsWith("/embed")
  ) {
    return normalized.toString().replace(/\/$/, "")
  }
  normalized.pathname = "/embed"
  normalized.search = ""
  normalized.hash = ""
  return normalized.toString().replace(/\/$/, "")
}

export function buildChatAppLaunchPayload({
  accessToken,
  agent,
  launchToken,
  openclaw,
  templateBuildId,
  templateCharacterProfile,
  userId,
}) {
  return {
    type: CHAT_APP_PLATFORM_AUTH_EVENT,
    payload: {
      accessToken,
      userId,
      agentId: agent.id,
      agentName: agent.name,
      ...(launchToken ? { launchToken } : {}),
      ...(openclaw?.connectionId
        ? { openclawConnectionId: openclaw.connectionId }
        : {}),
      ...(namespacePersonalAgentId(openclaw?.agentId)
        ? { openclawAgentId: namespacePersonalAgentId(openclaw?.agentId) }
        : {}),
      ...(openclaw?.agentName ? { openclawAgentName: openclaw.agentName } : {}),
      ...(templateBuildId ? { templateBuildId } : {}),
      ...(getCharacterPlatformApiUrlFromEnv()
        ? { platformApiUrl: getCharacterPlatformApiUrlFromEnv() }
        : {}),
      ...(templateCharacterProfile
        ? { templateCharacterProfile }
        : {}),
    },
  }
}

export function parseChatAppSessionOverride(rawValue) {
  if (!rawValue) return null

  try {
    const parsed = JSON.parse(rawValue)
    const accessToken = parsed?.accessToken?.trim?.() ?? null
    const userId = parsed?.userId?.trim?.() ?? null

    if (!accessToken || !userId) {
      return null
    }

    return { accessToken, userId }
  } catch {
    return null
  }
}

export function resolveChatAppSessionInfo({ session, overrideValue }) {
  const overrideSession = parseChatAppSessionOverride(overrideValue)
  if (overrideSession) {
    return overrideSession
  }

  return {
    accessToken: session?.access_token ?? null,
    userId: session?.user?.id ?? null,
  }
}

export function getChatAppEmbedAuthError(sessionInfo) {
  if (sessionInfo?.accessToken && sessionInfo?.userId) {
    return null
  }

  return "Sign in through character-platform to use chat."
}

export function getChatAppAgentSignature(agent) {
  const agentId = agent?.chatAppCharacterId?.trim?.() ?? agent?.id?.trim?.() ?? ""
  const agentName =
    agent?.characterConfig?.name?.trim?.() ?? agent?.name?.trim?.() ?? ""
  return `${agentId}:${agentName}`
}

export function getDefaultChatAppConnectionStatus() {
  return {
    connected: false,
    transportState: "disconnected",
  }
}

export function normalizeChatAppConnectionStatusPayload(payload) {
  const transportState =
    typeof payload?.transportState === "string" && payload.transportState.trim()
      ? payload.transportState.trim().toLowerCase()
      : "disconnected"
  const connected =
    typeof payload?.connected === "boolean"
      ? payload.connected
      : transportState === "connected" || transportState === "ready"

  return {
    connected,
    transportState,
  }
}

export function getChatAppConnectionIndicator(status) {
  const normalized = normalizeChatAppConnectionStatusPayload(status)
  if (normalized.connected) {
    return {
      color: "#22c55e",
      label: "Connected",
    }
  }

  if (
    ["authenticating", "connecting", "initializing", "reconnecting"].includes(
      normalized.transportState
    )
  ) {
    return {
      color: "#f59e0b",
      label: "Connecting…",
    }
  }

  return {
    color: "#ef4444",
    label: "Disconnected",
  }
}

export function isFailedPlatformTaskEvent(taskPayload) {
  if (!taskPayload || typeof taskPayload !== "object") {
    return false
  }

  const event = String(taskPayload.event ?? "").trim().toLowerCase()
  const error = String(taskPayload.error ?? "").trim()
  return event === "fail" || Boolean(error)
}

export function resolveCompletedPlatformTask({ agentId, taskPayload, snapshot }) {
  if (!agentId || !taskPayload || typeof taskPayload !== "object") {
    return null
  }

  const event = String(taskPayload.event ?? "").trim().toLowerCase()
  if (event !== "end" && event !== "complete") {
    return null
  }
  if (isFailedPlatformTaskEvent(taskPayload)) {
    return null
  }

  const completedTasks = Array.isArray(snapshot?.tasks?.completed)
    ? snapshot.tasks.completed
    : []
  const taskId = String(taskPayload?.task_id ?? taskPayload?.task?.id ?? "").trim()
  if (!taskId) {
    return null
  }

  return (
    completedTasks.find((task) => String(task?.id ?? "").trim() === taskId) || null
  )
}

export function resolveFailedPlatformTask({ agentId, taskPayload, snapshot }) {
  if (!agentId || !isFailedPlatformTaskEvent(taskPayload)) {
    return null
  }

  const completedTasks = Array.isArray(snapshot?.tasks?.completed)
    ? snapshot.tasks.completed
    : []
  const taskId = String(taskPayload?.task_id ?? taskPayload?.task?.id ?? "").trim()
  const error = String(taskPayload?.error ?? "").trim()
  const errorDetails =
    taskPayload?.error_details && typeof taskPayload.error_details === "object"
      ? taskPayload.error_details
      : null

  const matchingTask =
    completedTasks.find((task) => String(task?.id ?? "").trim() === taskId)
    || completedTasks.find(
      (task) =>
        String(task?.status ?? "").trim().toLowerCase() === "failed"
        && error
        && String(task?.error ?? "").trim() === error
    )
    || completedTasks.find(
      (task) => String(task?.status ?? "").trim().toLowerCase() === "failed"
    )
  if (matchingTask) {
    if (!matchingTask.error_details && errorDetails) {
      return {
        ...matchingTask,
        error_details: errorDetails,
      }
    }
    return matchingTask
  }

  const label =
    String(taskPayload?.task?.label ?? "").trim()
    || String(taskPayload?.tool_use?.label ?? "").trim()
    || String(taskPayload?.tool_use?.toolLabel ?? "").trim()
    || "OpenClaw task failed"
  const instruction = String(taskPayload?.task?.instruction ?? "").trim()
  const now = Date.now()

  return {
    id: taskId || `chat-app-error-${now}`,
    agentId,
    label,
    instruction: instruction || null,
    status: "failed",
    createdAt: now,
    startedAt: now,
    completedAt: now,
    steps: [],
    result: null,
    error: error || "Unknown error",
    error_details: errorDetails,
  }
}

export function resolveChatAppEmbedUrl({ configuredUrl, overrideUrl }) {
  const candidate = overrideUrl || configuredUrl
  return candidate ? buildChatAppEmbedUrl(candidate) : null
}
